import { PurposeRestriction, RestrictionType, TCModel, TCString } from '@iabtcf/core'
import { createLog } from '../utils/logger'

const log = createLog()

async function createTcModelFromGvl (gvl) {
  log('creating TCModel for new user')
  const tcModel = new TCModel()
  tcModel.cmpId = 27
  tcModel.cmpVersion = 3
  // siteSpecific Storage (ie not global)
  tcModel.isServiceSpecific = true
  tcModel.publisherCountryCode = 'GB'
  tcModel.gvl = gvl
  tcModel.publisherConsents.set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
  tcModel.publisherLegitimateInterests.set([5, 6, 8, 10])

  await tcModel.gvl.readyPromise

  const vendors = Object.values(tcModel.gvl.vendors).map(({ id, flexiblePurposes, legIntPurposes }) => ({ vendorId: id, flexiblePurposes: new Set(flexiblePurposes), legIntPurposes: new Set(legIntPurposes) }))
    ;[3, 4].forEach(purposeId => {
    const purposeRestriction = new PurposeRestriction()
    purposeRestriction.purposeId = purposeId
    purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
    vendors.forEach(({ vendorId, flexiblePurposes, legIntPurposes }) => {
      try {
        if (flexiblePurposes.has(purposeId) && legIntPurposes.has(purposeId)) {
          // vendorID and restriction
          log(`restricting purposeId: ${purposeId} for vendorId: ${vendorId}`)
          tcModel.publisherRestrictions.add(vendorId, purposeRestriction)
        }
      } catch (e) {
        log('unable to restrict', purposeId, vendorId)
      }
    })
  })

  const specialPurposeOnlyDeclared = Object.values(tcModel.gvl.vendors).filter(v => v.purposes.length === 0 && v.legIntPurposes.length === 0 && v.specialPurposes.length > 0)
  log('specialPurposeOnly', specialPurposeOnlyDeclared)

  return new TcModel({ gvl, model: tcModel, specialPurposeOnlyDeclared })
}

function tcModelFromTcString (gvl, tcString) {
  log('creating TCModel from tcString')
  const tcModel = TCString.decode(tcString)

  // controller was originally released with consentScreen=0, this hack will update all those users
  if (tcModel.consentScreen === 0) {
    tcModel.consentScreen = 1
  }
  tcModel.gvl = gvl
  const specialPurposeOnlyDeclared = Object.values(tcModel.gvl.vendors).filter(v => v.purposes.length === 0 && v.legIntPurposes.length === 0 && v.specialPurposes.length > 0)
  log('specialPurposeOnly', specialPurposeOnlyDeclared)
  return [tcModel, specialPurposeOnlyDeclared]
}

function createTcModelFromTcString (gvl, tcString) {
  const [tcModel, specialPurposeOnlyDeclared] = tcModelFromTcString(gvl, tcString)
  return new TcModel({ gvl, model: tcModel, specialPurposeOnlyDeclared })
}

class TcModel {
  constructor ({ gvl, model, specialPurposeOnlyDeclared = [] }) {
    this.gvl = gvl
    this.model = model
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
    this.byPurposeVendorMap = this.byPurposeVendorMap.bind(this)
    this.gvlIsReady = this.gvlIsReady.bind(this)
    this.updated = this.updated.bind(this)
    this.tcString = this.tcString.bind(this)
    this.updateUserPreferences = this.updateUserPreferences.bind(this)
    this.consentAll = this.consentAll.bind(this)
    this.dissentAll = this.dissentAll.bind(this)
    this.hasUserDissentedToAll = this.hasUserDissentedToAll.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.consentedVendorCheck = this.consentedVendorCheck.bind(this)
  }

  reload (tcString) {
    const [newModel, specialPurposeOnlyDeclared] = tcModelFromTcString(this.gvl, tcString)
    log('reloading tcModel', tcString, newModel)

    this.model = newModel
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
  }

  byPurposeVendorMap () {
    return this.model.gvl.byPurposeVendorMap
  }

  gvlIsReady () {
    return this.model.gvl.readyPromise
  }

  updated () {
    this.model.updated()
  }

  tcString () {
    log('encoding', this.model)
    return TCString.encode(this.model)
  }

  updateUserPreferences ({ vendorConsents, vendorLegitimateInterests, purposeConsents, purposeLegitimateInterests, specialFeatureOptins, consentScreen }) {
    this.model.vendorConsents.empty()
    this.model.vendorConsents.set(vendorConsents)

    this.model.vendorLegitimateInterests.empty()
    this.model.vendorLegitimateInterests.set(vendorLegitimateInterests)
    this.model.vendorLegitimateInterests.set(this.specialPurposeOnlyDeclared.map(({ id }) => id))

    this.model.purposeConsents.empty()
    this.model.purposeConsents.set(purposeConsents)
    this.model.purposeLegitimateInterests.empty()
    this.model.purposeLegitimateInterests.set(purposeLegitimateInterests)

    this.model.specialFeatureOptins.empty()
    this.model.specialFeatureOptins.set(specialFeatureOptins)

    this.model.consentScreen = consentScreen
    this.model.updated()
  }

  consentAll ({ consentScreen }) {
    this.model.setAll()
    this.model.consentScreen = consentScreen
    this.model.updated()
  }

  dissentAll ({ consentScreen }) {
    this.model.unsetAll()
    this.model.setAllPurposeLegitimateInterests()
    this.model.setAllVendorLegitimateInterests()
    this.model.consentScreen = consentScreen
    this.model.updated()
  }

  hasUserDissentedToAll () {
    return !this.model.specialFeatureOptins.size &&
      !this.model.purposeConsents.size &&
      !this.model.vendorConsents.size
  }

  hasUserConsentedToAll () {
    const vendorsWithPurposes = Object.keys(this.model.gvl.vendors).filter(id => this.model.gvl.vendors[id].purposes.length > 0).map(id => parseInt(id))
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const specialFeatureOptinIds = Object.keys(this.model.gvl.specialFeatures).map(id => parseInt(id))
    const hasConsentedToAllSpecialFeatureOptins = specialFeatureOptinIds.every(id => this.model.specialFeatureOptins.has(id))
    const hasConsentedToAllPurposes = purposesIds.every(id => this.model.purposeConsents.has(id))
    const hasConsentedToAllVendorPurposes = vendorsWithPurposes.every(id => this.model.vendorConsents.has(id))
    return hasConsentedToAllVendorPurposes && hasConsentedToAllPurposes && hasConsentedToAllSpecialFeatureOptins
  }

  consentedVendorCheck () {
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const purposeRestrictions = [3, 4].map(purposeId => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      return purposeRestriction
    })
    const model = TCString.decode(TCString.encode(this.model))
    const vendorIdsDissentedByPurpose = new Set(purposesIds.filter(id => !model.purposeConsents.has(id)).map(id => Array.from(this.model.gvl.byPurposeVendorMap[id] && this.model.gvl.byPurposeVendorMap[id].consent || [])).flat())
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some(restriction => {
        return model.publisherRestrictions.vendorHasRestriction(id, restriction) && !model.purposeConsents.has(restriction.purposeId)
      })
    }
    const preferenceForIds = Array.from(this.model.gvl.vendorIds).map(id => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const vendorRestrictedToConsent = purposeRestrictions.some(restriction => model.publisherRestrictions.vendorHasRestriction(id, restriction))
      const hasConsented = (vendorRestrictedToConsent ? model.vendorConsents.has(id) : (model.vendorConsents.has(id) || model.vendorLegitimateInterests.has(id))) &&
        !vendorIdsDissentedByPurpose.has(id) &&
        !constrainedByFlexPurpose

      return {
        id,
        hasConsented,
        consentedTests: {
          consented: this.model.vendorConsents.has(id),
          legitimateInterest: this.model.vendorLegitimateInterests.has(id),
          vendorRestrictedToConsent,
          dissentedByPurpose: vendorIdsDissentedByPurpose.has(id),
          constrainedByFlexPurpose: constrainedByFlexPurpose
        }
      }
    })
    return preferenceForIds
  }

  consentedVendors () {
    return this.consentedVendorCheck().filter(({ hasConsented }) => hasConsented).map(({ id }) => id)
  }

  consentedVendorsDetails () {
    return this.consentedVendorCheck()
  }
}

export async function createTcModel ({ tcString, gvl, oldModel }) {
  const tcModel = await (tcString ? createTcModelFromTcString(gvl, tcString) : createTcModelFromGvl(gvl))
  if (oldModel) {
    const preferences = [
      'purposeConsents',
      'purposeLegitimateInterests',
      'specialFeatureOptins',
      'vendorConsents',
      'vendorLegitimateInterests'
    ].reduce((prefs, key) => ({ ...prefs, ...{ [key]: Array.from(oldModel.model[key].set_).map(v => parseInt(v)) } }), {})
    preferences.consentScreen = oldModel.model.consentScreen_
    tcModel.updateUserPreferences(preferences)
  }
  return tcModel
}
